import type {
    PtV2Action,
    PtV2ActionInput,
    PtV2InitiativeOptions,
    PtV2InitiativeSource,
} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Classes.ts";
import {formatEurFull} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Format.ts";

/** The SIT default priority; `create_draft_initiative` takes priority labels (`~/sit/ApiTypes` `Priority`). */
export const PT_V2_DEFAULT_PRIORITY = 'Medium';

/** SIT initiative titles are capped at 256 characters by the backend. */
const TITLE_MAX = 256;

export type PtV2InitiativeDraft = {title: string; description: string};

/**
 * Title and description for a savings initiative made from a plan item.
 * With a supplier source: `Payment terms: <supplier> → <target>` and the terms, value and recommended action.
 * Without one (a fixed task, or an item from before iteration 2), the action's own title.
 * `pageUrl` links back to the tab the item came from.
 */
export function buildInitiativeDraft(
    action: Pick<PtV2ActionInput, 'title' | 'subtitle' | 'value'>,
    source: PtV2InitiativeSource | null | undefined,
    pageUrl: string,
): PtV2InitiativeDraft {
    if (!source) {
        const lines = [
            action.subtitle ? `${action.subtitle}: ${action.title}` : action.title,
            action.value != null ? `Working capital: ${formatEurFull(action.value)}` : null,
            '',
            `Source: Payment Terms in Mithra, ${pageUrl}`,
        ];
        return {
            title: `Payment terms: ${action.title}`.slice(0, TITLE_MAX),
            description: lines.filter(l => l != null).join('\n'),
        };
    }
    const lines = [
        `Supplier: ${source.supplier}`,
        `Current term: ${source.currentTerm}`,
        `Target term: ${source.targetCode}`,
        source.value != null ? `Working capital: ${formatEurFull(source.value)}` : null,
        source.recommendedAction ? `Recommended action: ${source.recommendedAction}` : null,
        '',
        `Source: Payment Terms in Mithra, ${pageUrl}`,
    ];
    return {
        title: `Payment terms: ${source.supplier} → ${source.targetCode}`.slice(0, TITLE_MAX),
        description: lines.filter(l => l != null).join('\n'),
    };
}

/** Why an initiative cannot be created right now, or null when it can. */
export function initiativeBlockedReason(options: PtV2InitiativeOptions | undefined): string | null {
    if (!options) return 'Checking the Savings Initiative Tracker…';
    if (!options.enabled) return options.reason ?? 'The Savings Initiative Tracker is not enabled for this environment.';
    if (!options.hasProfile) return options.reason ?? 'You need a Savings Initiative Tracker profile to create initiatives.';
    return null;
}

/**
 * Where an action's initiative opens: an in-app path when the URL is relative or on this origin,
 * else the absolute URL. Falls back to the SIT route for the id.
 */
export function initiativeLink(
    action: Pick<PtV2Action, 'initiativeId' | 'initiativeUrl'>,
    origin: string,
): {href: string; external: boolean} | null {
    const url = action.initiativeUrl?.trim();
    if (url) {
        if (!/^https?:\/\//i.test(url)) return {href: url.startsWith('/') ? url : `/${url}`, external: false};
        try {
            const parsed = new URL(url);
            if (parsed.origin === origin) return {href: parsed.pathname + parsed.search + parsed.hash, external: false};
            return {href: url, external: true};
        } catch {
            return null;
        }
    }
    // `sit_initiative` in `~/sit/routing/sitRoutes.ts`; not imported, as that module pulls in every SIT page.
    return action.initiativeId != null ? {href: `/savings-tracker/initiatives/${action.initiativeId}`, external: false} : null;
}
